import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Copy, CheckCircle, CurrencyDollar, Link as LinkIcon, ArrowClockwise, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const AdminRetainerLinks = () => {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [amount, setAmount] = useState("");
  const [interval, setInterval] = useState("month");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState(null);

  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);

  const fetchLinks = async () => {
    setLoadingLinks(true);
    try {
      const res = await axios.get(`${API}/admin/retainer-links`);
      setLinks(res.data.links || []);
    } catch (err) {
      console.error("Failed to load retainer links:", err);
      toast.error("Couldn't load existing retainer links.");
    } finally {
      setLoadingLinks(false);
    }
  };

  useEffect(() => { fetchLinks(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!clientName.trim()) return toast.error("Client name is required.");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Amount must be greater than 0.");

    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/retainer-link`, {
        client_name: clientName.trim(),
        client_email: clientEmail.trim() || null,
        amount_dollars: amt,
        interval,
        description: description.trim() || null,
      });
      setJustCreated(res.data);
      toast.success(res.data.reused
        ? `Reused existing link for ${res.data.client_name}`
        : `Payment link created for ${res.data.client_name}!`);
      fetchLinks();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Failed: ${msg}`);
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  const deactivate = async (link) => {
    if (!window.confirm(`Deactivate the payment link for ${link.client_name}? Existing subscriptions will NOT be cancelled — this only prevents NEW signups on this link.`)) return;
    try {
      await axios.post(`${API}/admin/retainer-link/${link.payment_link_id}/deactivate`);
      toast.success("Link deactivated.");
      fetchLinks();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Failed: ${msg}`);
    }
  };

  const clearForm = () => {
    setClientName("");
    setClientEmail("");
    setAmount("");
    setInterval("month");
    setDescription("");
    setJustCreated(null);
  };

  const fmtCurrency = (dollars, iv) =>
    `$${dollars.toFixed(2)}/${iv === "year" ? "yr" : "mo"}`;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="admin-retainer-links-page">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <CurrencyDollar weight="duotone" className="w-8 h-8 text-emerald-400" />
            Retainer Payment Links
          </h1>
          <p className="text-zinc-400 mt-1 text-sm">
            Generate custom Stripe payment links for retainer clients (e.g., Charles $250/mo, Belva $199/mo).
            Same client + amount = reuses the existing link. Text the URL to the client to collect payment.
          </p>
        </div>

        {/* Create Form */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white text-lg">Create a new retainer link</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client-name" className="text-zinc-300">Client name *</Label>
                <Input
                  id="client-name"
                  data-testid="retainer-client-name-input"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Charles Campbell"
                  className="bg-zinc-900 border-zinc-800 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client-email" className="text-zinc-300">Client email <span className="text-zinc-600">(optional)</span></Label>
                <Input
                  id="client-email"
                  data-testid="retainer-client-email-input"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="charles@example.com"
                  className="bg-zinc-900 border-zinc-800 text-white"
                  type="email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount" className="text-zinc-300">Amount (USD) *</Label>
                <Input
                  id="amount"
                  data-testid="retainer-amount-input"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="250.00"
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="bg-zinc-900 border-zinc-800 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Billing interval</Label>
                <Select value={interval} onValueChange={setInterval}>
                  <SelectTrigger data-testid="retainer-interval-select" className="bg-zinc-900 border-zinc-800 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="year">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description" className="text-zinc-300">Description <span className="text-zinc-600">(optional — shown on Stripe checkout)</span></Label>
                <Textarea
                  id="description"
                  data-testid="retainer-description-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="FlashGrants Guided Retainer — monthly strategy call, priority text access, one application review per month."
                  className="bg-zinc-900 border-zinc-800 text-white"
                  rows={3}
                />
              </div>
              <div className="md:col-span-2 flex items-center gap-3">
                <Button
                  type="submit"
                  disabled={creating}
                  data-testid="retainer-create-btn"
                  className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
                >
                  {creating ? <><ArrowClockwise className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : <><LinkIcon className="w-4 h-4 mr-2" /> Generate Payment Link</>}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={clearForm}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-900"
                >
                  Clear
                </Button>
              </div>
            </form>

            {justCreated && (
              <div className="mt-6 p-4 rounded-md border border-emerald-500/40 bg-emerald-500/5" data-testid="retainer-just-created">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400" />
                  <p className="text-white font-semibold">
                    {justCreated.reused ? "Reused existing link" : "Payment link ready"} — {justCreated.client_name}, {fmtCurrency(justCreated.amount_dollars, justCreated.interval)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={justCreated.url}
                    onFocus={(e) => e.target.select()}
                    className="bg-zinc-900 border-zinc-800 text-emerald-300 font-mono text-xs"
                    data-testid="retainer-created-url"
                  />
                  <Button
                    onClick={() => copyToClipboard(justCreated.url)}
                    className="bg-zinc-800 hover:bg-zinc-700 text-white shrink-0"
                    data-testid="retainer-copy-created-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Text or email this link to {justCreated.client_name}. They&apos;ll enter their card on Stripe and be billed on Day 1, then every {justCreated.interval} until cancellation.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Existing Links */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center justify-between">
              <span>Existing retainer links ({links.length})</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchLinks}
                className="text-zinc-400 hover:text-white"
                data-testid="retainer-refresh-btn"
              >
                <ArrowClockwise className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingLinks ? (
              <p className="text-zinc-500 text-sm">Loading…</p>
            ) : links.length === 0 ? (
              <p className="text-zinc-500 text-sm italic">No retainer links yet. Create your first one above.</p>
            ) : (
              <div className="space-y-2">
                {links.map((link) => (
                  <div
                    key={link.payment_link_id}
                    className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-3 rounded-md bg-zinc-900 border border-zinc-800"
                    data-testid={`retainer-link-row-${link.client_slug}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-medium">
                        {link.client_name}
                        <span className="ml-2 text-emerald-400 font-mono text-sm">
                          {fmtCurrency(link.amount_dollars, link.interval)}
                        </span>
                        {link.active === false && (
                          <span className="ml-2 text-[10px] uppercase tracking-widest text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded-sm">Inactive</span>
                        )}
                      </p>
                      <a
                        href={link.payment_link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-zinc-500 hover:text-emerald-400 font-mono break-all"
                      >
                        {link.payment_link_url}
                      </a>
                      {link.client_email && (
                        <p className="text-xs text-zinc-600 mt-1">{link.client_email}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => copyToClipboard(link.payment_link_url)}
                        className="bg-zinc-800 hover:bg-zinc-700 text-white"
                        data-testid={`retainer-copy-${link.client_slug}`}
                      >
                        <Copy className="w-4 h-4 mr-1" /> Copy
                      </Button>
                      {link.active !== false && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deactivate(link)}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          data-testid={`retainer-deactivate-${link.client_slug}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Deactivate
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminRetainerLinks;
